import { API_ENDPOINTS } from '../config/apiConfig';
import apiClient from './apiClient';
import { createKeyedInFlightShare } from '../utils/shareInFlightRequest';

/**
 * Effective global DMS presentation summary - not scoped to a specific RTU.
 * Backend enforcement is authoritative regardless of what this reports; it is
 * used only to decide what the frontend shows (see DmsAuthorizationPolicyService
 * on the backend).
 */
export interface DmsAccessSummary {
  operator: boolean;
  administrator: boolean;
  accessUnlinkedRtus: boolean;
}

export interface CurrentUserAccess {
  employeeKey: number | null;
  id: number | null;
  roles: string[];
  capabilities: string[];
  dms: DmsAccessSummary;
}

const CURRENT_USER_IN_FLIGHT_KEY = 'GET /security/current-user';

/**
 * Max age of a settled result before it's treated as expired (checked by wall
 * clock, not by any scheduled timer - a background tab throttles timers, so a
 * timer's firing time is a nudge, not the enforcement mechanism). Not an
 * end-to-end freshness guarantee: CurrentUserAccessController returns
 * roles=List.of() (always empty) and computes the DMS operator/administrator
 * summary from the database on every call. JdbcUserCapabilityService normally
 * caches capabilities for about five minutes; upsertCapabilityGrant invalidates
 * that backend cache immediately, so only capability changes made outside that
 * write path can lag behind this TTL. The frontend 30-second TTL is therefore
 * not always an end-to-end 30-second permission-change guarantee.
 */
export const CURRENT_USER_ACCESS_TTL_MS = 30_000;

/** Shared delay after a failed request so a row mounted mid-backoff waits instead of retrying on its own. */
export const CURRENT_USER_ACCESS_RETRY_DELAY_MS = 5_000;

/** How long before TTL expiry to proactively re-check, so a healthy refresh usually lands before evidence goes stale. */
export const CURRENT_USER_ACCESS_PROACTIVE_LEAD_MS = 5_000;

const currentUserInFlight = createKeyedInFlightShare<CurrentUserAccess>();
const cacheListeners = new Set<() => void>();
let cachedCurrentUserAccess: CurrentUserAccess | null = null;
let cachedExpiresAtMs = 0;
let cachedIdentityKey: string | null = null;
let currentUserCacheGeneration = 0;
let lastFailureAtMs = 0;
let lastIdentityKey: string | null = null;
let logoutListenerBound = false;

const isInFailureBackoff = (): boolean => (
  lastFailureAtMs > 0 && Date.now() < lastFailureAtMs + CURRENT_USER_ACCESS_RETRY_DELAY_MS
);

const clearSettledCache = () => {
  cachedCurrentUserAccess = null;
  cachedExpiresAtMs = 0;
  cachedIdentityKey = null;
};

const notifyCacheListeners = () => {
  cacheListeners.forEach((listener) => listener());
};

/** Cache is only usable for the identity it was fetched for, within the TTL, and not during failure backoff. */
const getFreshSettledCache = (identityKey: string): CurrentUserAccess | null => {
  if (isInFailureBackoff()) {
    return null;
  }
  if (!cachedCurrentUserAccess || cachedIdentityKey !== identityKey) {
    return null;
  }
  if (Date.now() >= cachedExpiresAtMs) {
    clearSettledCache();
    return null;
  }
  return cachedCurrentUserAccess;
};

const toNumberOrNull = (value: unknown): number | null => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toStringArray = (value: unknown): string[] => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map((entry) => String(entry ?? '').trim())
    .filter(Boolean);
};

const toBoolean = (value: unknown): boolean => value === true;

const toDmsAccessSummary = (value: unknown): DmsAccessSummary => {
  const raw = value as any;
  return {
    operator: toBoolean(raw?.operator),
    administrator: toBoolean(raw?.administrator),
    accessUnlinkedRtus: toBoolean(raw?.accessUnlinkedRtus),
  };
};

const mapCurrentUserAccess = (raw: any): CurrentUserAccess => {
  const employeeKey = toNumberOrNull(raw?.employeeKey);
  return {
    employeeKey,
    id: toNumberOrNull(raw?.id) ?? employeeKey,
    roles: toStringArray(raw?.roles),
    capabilities: toStringArray(raw?.capabilities),
    dms: toDmsAccessSummary(raw?.dms),
  };
};

const bindLogoutInvalidation = () => {
  if (logoutListenerBound || typeof window === 'undefined') {
    return;
  }
  logoutListenerBound = true;
  window.addEventListener('dms-auth-logout', () => {
    currentUserAccessService.invalidate();
  });
};

/**
 * Fetches and caches, keyed per-identity so a request for one identity is
 * never joined by a caller for a different one. A generation captured before
 * the request is compared after it settles; if invalidate() ran in between
 * (logout / account change), the result is rejected as superseded rather than
 * cached or returned. This applies to both success and failure, so a stale
 * request can never poison the cache, the failure backoff, or a consumer's
 * displayed grant.
 */
const fetchAndCache = (identityKey: string): Promise<CurrentUserAccess> => {
  const key = `${CURRENT_USER_IN_FLIGHT_KEY}:${identityKey}`;
  return currentUserInFlight.run(key, async () => {
    const generation = currentUserCacheGeneration;
    let response;
    try {
      response = await apiClient.get(API_ENDPOINTS.SECURITY.CURRENT_USER);
    } catch (error) {
      if (generation === currentUserCacheGeneration) {
        clearSettledCache();
        lastFailureAtMs = Date.now();
        notifyCacheListeners();
      }
      throw error;
    }
    if (generation !== currentUserCacheGeneration) {
      throw new Error('current-user request superseded by a session change');
    }
    const mapped = mapCurrentUserAccess(response.data);
    cachedCurrentUserAccess = mapped;
    cachedExpiresAtMs = Date.now() + CURRENT_USER_ACCESS_TTL_MS;
    cachedIdentityKey = identityKey;
    lastFailureAtMs = 0;
    return mapped;
  });
};

const currentUserAccessService = {
  peek(identityKey: string): CurrentUserAccess | null {
    return getFreshSettledCache(identityKey);
  },

  getExpiresAtMs(identityKey: string): number | null {
    if (!getFreshSettledCache(identityKey)) {
      return null;
    }
    return cachedExpiresAtMs;
  },

  getRemainingTtlMs(identityKey: string): number {
    const expiresAtMs = this.getExpiresAtMs(identityKey);
    if (expiresAtMs === null) {
      return 0;
    }
    return Math.max(0, expiresAtMs - Date.now());
  },

  /** Remaining backoff after a failed fetch, or 0 if a new attempt is allowed. */
  getRetryAfterMs(): number {
    if (!lastFailureAtMs) {
      return 0;
    }
    return Math.max(0, lastFailureAtMs + CURRENT_USER_ACCESS_RETRY_DELAY_MS - Date.now());
  },

  subscribe(listener: () => void): () => void {
    cacheListeners.add(listener);
    return () => {
      cacheListeners.delete(listener);
    };
  },

  invalidate() {
    currentUserCacheGeneration += 1;
    clearSettledCache();
    lastFailureAtMs = 0;
    lastIdentityKey = null;
    currentUserInFlight.reset();
    notifyCacheListeners();
  },

  /**
   * Tracks the authenticated identity. Invalidates on account change, not on
   * same-identity token renewal. First observation is a baseline only.
   */
  syncSession(identityKey: string): boolean {
    const previous = lastIdentityKey;
    if (previous === identityKey) {
      return false;
    }
    lastIdentityKey = identityKey;
    if (previous === null) {
      return false;
    }
    this.invalidate();
    lastIdentityKey = identityKey;
    return true;
  },

  /** Cache-aware: backoff first, then fresh cached evidence, else fetch. */
  async getCurrentUserAccess(identityKey: string): Promise<CurrentUserAccess> {
    bindLogoutInvalidation();
    const retryAfterMs = this.getRetryAfterMs();
    if (retryAfterMs > 0) {
      throw new Error(`current-user request backing off for ${retryAfterMs}ms after a prior failure`);
    }
    const cached = getFreshSettledCache(identityKey);
    if (cached) {
      return cached;
    }
    return fetchAndCache(identityKey);
  },

  /** Bypasses the cache to force a network re-check (still subject to backoff and in-flight sharing). */
  async forceRefresh(identityKey: string): Promise<CurrentUserAccess> {
    bindLogoutInvalidation();
    const retryAfterMs = this.getRetryAfterMs();
    if (retryAfterMs > 0) {
      throw new Error(`current-user request backing off for ${retryAfterMs}ms after a prior failure`);
    }
    return fetchAndCache(identityKey);
  },
};

bindLogoutInvalidation();

export default currentUserAccessService;
